import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, ShieldCheck, Clock, Sparkles } from "lucide-react";
import { useLang, waLink } from "@/i18n";

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const badgeIcons = [ShieldCheck, Clock, Sparkles];

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden grain" data-testid="hero-section">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src="/assets/amer-fort.jpg" alt="Amer Fort illuminated at night, Jaipur" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C10]/80 via-[#0B0C10]/55 to-[#0B0C10]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(11,12,16,0.55)_100%)]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 pt-32 pb-24 w-full">
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-xs sm:text-sm uppercase tracking-[0.35em] font-mono text-gold-amber mb-6"
          data-testid="hero-kicker"
        >
          {t.hero.kicker}
        </motion.p>

        <h1 className="font-display font-medium text-5xl sm:text-7xl lg:text-[6.5rem] leading-[0.98] tracking-tight text-slate-50" data-testid="hero-headline">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                className={`block ${i === 1 ? "text-gold-gradient italic" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300"
          data-testid="hero-subtext"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#enquiry"
            data-testid="hero-enquire-now-button"
            className="inline-flex items-center rounded-full bg-gradient-to-r from-gold to-gold-amber px-8 py-4 text-sm sm:text-base font-bold text-obsidian hover:shadow-[0_0_36px_rgba(212,175,55,0.5)] hover:-translate-y-0.5 transition-all"
          >
            {t.hero.ctaPrimary}
          </a>
          <a
            href={waLink(0, "Namaste Explore India! I would like to plan a journey.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-8 py-4 text-sm sm:text-base font-semibold text-gold-light hover:bg-gold/10 hover:border-gold/70 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2zm0 18.03c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.26 8.26 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 4.54 0 8.24 3.7 8.24 8.24 0 4.55-3.7 8.24-8.24 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.17.25-.64.8-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.11-.22-.17-.47-.29z"/></svg>
            {t.hero.ctaSecondary}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.35 }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3"
          data-testid="hero-trust-badges"
        >
          {t.hero.badges.map((b, i) => {
            const Icon = badgeIcons[i];
            return (
              <span key={b} className="flex items-center gap-2 text-xs sm:text-sm text-slate-400">
                <Icon size={15} className="text-gold" />
                {b}
              </span>
            );
          })}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-slate-500"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] font-mono">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown size={18} className="text-gold/70" />
        </motion.span>
      </motion.div>
    </section>
  );
};

export default Hero;
